import { getTagColors } from '@/components/ui/color-picker';
import { Badge } from '@/components/ui/design-system';
import { Select, SelectContent, SelectItem } from '@/components/ui/select';
import type { components } from '@/services/api-allo/api-allo-v1';
import * as SelectPrimitive from '@radix-ui/react-select';
import * as React from 'react';
import { TableCell } from '../table-cell';

type TagResponse = components['schemas']['TagResponse'];

function TagDisplay({ tag }: { tag: TagResponse | undefined }) {
  if (!tag) {
    return null;
  }
  return <Badge colors={getTagColors(tag.hex_color, tag.background_hex_color)}>{tag.name}</Badge>;
}

export interface TagCellHandle {
  open: () => void;
}

interface TagCellProps {
  tags: TagResponse[];
  value?: string;
  onValueChange?: (contactId: string, columnKey: string, value: string) => void;
  contactId?: string;
  columnKey: string;
}

export const TagCell = React.memo(
  React.forwardRef<TagCellHandle, TagCellProps>(function TagCell(
    { tags, value, onValueChange, contactId, columnKey },
    ref,
  ) {
    const [open, setOpen] = React.useState(false);
    const wrapperRef = React.useRef<HTMLDivElement>(null);
    const allowOpenRef = React.useRef(false);

    const selectedTag = tags.find((tag) => tag.key === value);
    const isEditable = !!contactId && !!onValueChange;

    const requestOpen = React.useCallback(() => {
      allowOpenRef.current = true;
      setOpen(true);
    }, []);

    React.useImperativeHandle(ref, () => ({ open: requestOpen }), [requestOpen]);

    const handleOpenChange = React.useCallback(
      (newOpen: boolean) => {
        if (newOpen && !allowOpenRef.current) {
          return;
        }
        allowOpenRef.current = false;
        if (newOpen === open) {
          return;
        }
        setOpen(newOpen);
        if (!newOpen) {
          const cell = wrapperRef.current?.closest('[tabindex]') as HTMLElement | null;
          if (cell) {
            requestAnimationFrame(() => cell.focus());
          }
        }
      },
      [open],
    );

    const sortedTags = React.useMemo(
      () =>
        [...tags].sort((a, b) => {
          if (a.key === value) {
            return -1;
          }
          if (b.key === value) {
            return 1;
          }
          return 0;
        }),
      [tags, value],
    );

    if (!isEditable) {
      return (
        <TableCell className="h-10">
          <TagDisplay tag={selectedTag} />
        </TableCell>
      );
    }

    return (
      <Select
        open={open}
        onOpenChange={handleOpenChange}
        value={value}
        onValueChange={(v) => onValueChange(contactId, columnKey, v)}
      >
        <SelectPrimitive.Trigger asChild>
          <div ref={wrapperRef} onDoubleClick={requestOpen}>
            <TableCell className="h-10">
              <TagDisplay tag={selectedTag} />
            </TableCell>
          </div>
        </SelectPrimitive.Trigger>
        <SelectContent
          disableScroll
          side="bottom"
          align="start"
          sideOffset={-41}
          className="mb-0 p-1 py-0 w-[248px] max-h-[192px] border-primary-5 shadow-lg"
        >
          {sortedTags.map(
            (tag) =>
              tag.key && (
                <SelectItem
                  key={tag.key}
                  className="cursor-pointer w-full pl-1 py-0.5 rounded-md focus:bg-primary-3 font-medium first:mt-2 last:mb-2"
                  value={tag.key}
                >
                  <TagDisplay tag={tag} />
                </SelectItem>
              ),
          )}
        </SelectContent>
      </Select>
    );
  }),
);
